/**
 * Input variant styles (class-variance-authority)
 * @layer atom
 * @package @plerity/ui-core
 *
 * Extracted to a leaf module so Input.tsx and Input.types.ts can both import
 * `inputVariants` without forming an Input.tsx <-> Input.types.ts import cycle
 * (import-cycles-packages baseline, 2026-09-15). Re-exported from Input.tsx so
 * the public `@plerity/ui-core` import path is unchanged.
 */

import { cva } from "class-variance-authority";

export const inputVariants = cva(
  "block w-full rounded-[var(--app-input-radius,0.375rem)] border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        // Literal white/gray-800 surface — do NOT use `bg-surface` or
        // `border-[var(--color-border-default)]` / `text-[var(--color-text-primary)]`.
        // Those emit `var(--color-*)` with NO fallback and are stripped from the
        // React SPA apps' Tailwind v4 build. The literal ramp keeps fallbacks so
        // dark mode renders true-dark and survives the strip.
        // Border: `border-control` (tokens-apps preset alias, literal fallback)
        // clears WCAG 1.4.11 3:1 in both themes; gray-200/700 measured ~1.3:1
        // (backlog tokens-apps-control-border-token-2026-10-26-001).
        default:
          "border-control bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500 focus-visible:ring-brand-focus dark:bg-gray-800 dark:text-gray-100",
        error:
          "border-red-500 bg-white text-gray-900 placeholder-gray-400 focus:border-red-500 focus-visible:ring-red-500 dark:border-red-400 dark:bg-gray-800 dark:text-gray-100",
      },
      size: {
        sm: "px-[var(--app-input-padding-x-sm,0.625rem)] py-[var(--app-input-padding-y-sm,0.375rem)] text-[length:var(--app-input-font-size-sm,0.875rem)]",
        md: "px-[var(--app-input-padding-x-md,0.75rem)] py-[var(--app-input-padding-y-md,0.5rem)] text-[length:var(--app-input-font-size-md,0.875rem)]",
        lg: "px-[var(--app-input-padding-x-lg,1rem)] py-[var(--app-input-padding-y-lg,0.625rem)] text-[length:var(--app-input-font-size-lg,1rem)]",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  }
);
